import { convertDatesToISO, getHeaders } from "@/config/aux";
import { Device } from "../models/device";
import { DeviceStateType } from "./state";
import type { DeviceSample } from "../../DeviceSamples/models/deviceSample";
import axios from "axios";

export interface DeviceActionsType {
  getDevices(): Promise<void>;
  getDevicesByLocationIds(locationIds: string[]): Promise<void>;
  getDeviceById(id: string): Promise<Device | null>;
  getDeviceMeasurements(deviceId: string): Promise<DeviceSample[]>;
  getDeviceLastMeasurement(deviceId: string): Promise<DeviceSample | null>;
  postDevice(device: Device): Promise<void>;
  putDevice(device: Device): Promise<void>;
  deleteDevice(id: string): Promise<void>;
}

export const DeviceActions = {
  async getDevices(this: DeviceStateType) {
    try {
      console.log("Fetching devices...");
      this.devices = [];
      const query = `${process.env.VUE_APP_URL_BASE}/devices`;
      await axios.get(query, { headers: getHeaders() }).then((response) => {
        if (response.status === 200) {
          console.log("Devices fetched:", response.data);
          response.data.forEach((deviceMeasurements: Partial<Device>) => {
            this.devices.push(new Device(deviceMeasurements));
          });
        }
      });
    } catch (error) {
      // Error handling - podría mostrar notificación
    }
  },
  async getDevicesByLocationIds(
    this: DeviceStateType,
    locationIds: string[]
  ): Promise<void> {
    try {
      console.log("Fetching devices for locations:", locationIds);
      this.devices = [];

      const query = `${process.env.VUE_APP_URL_BASE}/devices/by-locations`;
      const response = await axios.post(
        query,
        locationIds,
        { headers: getHeaders() }
      );

      if (response.status === 200) {
        console.log("Devices fetched:", response.data);
        response.data.forEach((deviceData: Partial<Device>) => {
          this.devices.push(new Device(deviceData));
        });
      }
    } catch (error) {
      console.error('Error getting devices by locations', error);
      throw error;
    }
  },
  async getDeviceById(
    this: DeviceStateType,
    id: string
  ): Promise<Device | null> {
    try {
      const query = `${process.env.VUE_APP_URL_BASE}/devices/${id}`;
      const response = await axios.get(query, { headers: getHeaders() });
      if (response.status === 200) {
        return new Device(response.data);
      }
      return null;
    } catch (error) {
      throw error;
    }
  },
  async getDeviceMeasurements(
    this: DeviceStateType,
    deviceId: string
  ): Promise<DeviceSample[]> {
    try {
      // Asumiendo que el endpoint es algo como /device-measurements/{deviceId}/deviceSamples
      const query = `${process.env.VUE_APP_URL_BASE}/device-measurements/device/${deviceId}/deviceSamples`;
      const response = await axios.get(query, { headers: getHeaders() });
      if (response.status === 200) {
        const deviceSamples = response.data.map(
          (deviceSample: Partial<DeviceSample>) => ({
            ...deviceSample,
            createdAt: new Date(deviceSample.createdAt || new Date()),
            updatedAt: new Date(deviceSample.updatedAt || new Date()),
            managedAt: deviceSample.managedAt
              ? new Date(deviceSample.managedAt)
              : new Date(),
          })
        ) as DeviceSample[];

        // Guardar en el state
        this.deviceMeasurements[deviceId] = deviceSamples;
        return deviceSamples;
      }
      return [];
    } catch (error) {
      return [];
    }
  },
  async getDeviceLastMeasurement(
    this: DeviceStateType,
    deviceId: string
  ): Promise<DeviceSample | null> {
    try {
      const query = `${process.env.VUE_APP_URL_BASE}/device-measurements/device/${deviceId}/deviceSamples/latest`;
      const response = await axios.get(query, { headers: getHeaders() });
      if (response.status === 200) {
        const deviceSample = {
          ...response.data,
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
          managedAt: response.data.managedAt
            ? new Date(response.data.managedAt)
            : new Date(),
        } as DeviceSample;

        // Guardar en el state
        this.deviceLastMeasurement[deviceId] = deviceSample;
        return deviceSample;
      }
      return null;
    } catch (error) {
      return null;
    }
  },
  async postDevice(this: DeviceStateType, device: Device) {
    try {
      const query = `${process.env.VUE_APP_URL_BASE}/devices`;
      const data = device.toCreateObject(); // Usar toCreateObject para excluir el ID

      const response = await axios.post(query, data, { headers: getHeaders() });
      if (response.status === 201 || response.status === 200) {
        // Device created successfully
        return response.data;
      }
      throw new Error("Failed to create device");
    } catch (error) {
      console.error("Error creating device:", error);
      throw error;
    }
  },
  async putDevice(this: DeviceStateType, device: Device) {
    try {
      const query = `${process.env.VUE_APP_URL_BASE}/devices`;
      const data = convertDatesToISO(device.toPlainObject());

      const response = await axios.put(query, data, { headers: getHeaders() });
      if (response.status === 200 || response.status === 204) {
        // Device updated successfully
        return response.data;
      }
      throw new Error("Failed to update device");
    } catch (error) {
      console.error("Error updating device:", error);
      throw error;
    }
  },
  async deleteDevice(this: DeviceStateType, id: string) {
    try {
      const query = `${process.env.VUE_APP_URL_BASE}/devices/${id}`;
      const response = await axios.delete(query, { headers: getHeaders() });
      if (response.status === 200 || response.status === 204) {
        // Device deleted successfully
        return response.data;
      }
      throw new Error("Failed to delete device");
    } catch (error) {
      console.error("Error deleting device:", error);
      throw error;
    }
  },
};
